import { BigDataClickID, BigDataEventConstants } from "./constants/big-data-constants";
import { EventType } from "./constants/event-type-constants";
import { MarketingClientConstants } from "./constants/marketing-client-constants";
import { SwimmerConstants } from "./constants/swimmer-constants";
import { AllowedURLParamTypes } from "./custom-types/custom-types";
import { Attribute, BigDataHelper } from "./helpers/big-data-helper";
import { DeviceInformationHelper, DeviceType } from "./helpers/device-information-helper";
import { DOMHelper } from "./helpers/dom-helper";
import { BigData } from "./marketing-client-bigdata";
import { Capping } from "./marketing-client-capping";
import { Utilization } from "./marketing-client-utilization";
import { Utils } from "./utils/utils";

export abstract class MarketingClientRWBase {
    section: HTMLElement | null;
    isMobile: boolean;
    swimmer: HTMLElement | null;
    swimmerButton: HTMLElement | null;
    closeButton: HTMLElement | null;
    swimmerIsVisible: boolean;
    swimmerHeight: number | undefined | null;
    swimmerWasVisible = false;
    ddbox: HTMLElement | null;
    ddboxButton: HTMLElement | null;

    idSwimmer = "rw-swimmer";
    classSwimmerButton = ".rw_swimmer_button";
    ddboxSelector = "[data-ddbox-with-rw=true]";
    classDdboxButton = ".rw-ddbox-area";
    mobileCapHeight = 250;
    desktopCapHeight = 350;

    isHarmonyTest: AllowedURLParamTypes = Utils.getURLParamsAsObject().enableHarmonyTest;
    squidHomeUrl = this.isHarmonyTest ? "https://coreos07.wyborcza.pl:8094/" : "https://squid-api.wyborcza.pl/";

    constructor(
        public capping: Capping,
        public utilizationObject: Utilization,
        public classSection: string
    ) {
        this.isMobile = DeviceInformationHelper.getDeviceType() === DeviceType.MOBILE;
    }

    initialize(): void {
        this.initializeDdbox();
        this.initializeSwimmer();
        this.initializeSection();
    }

    initializeSection(): void {
        this.section = DOMHelper.querySelector(`section${this.classSection}`) as HTMLElement;
        this.hideSection();
    }

    initializeSwimmer(): void {
        this.swimmer = DOMHelper.getElementById(this.idSwimmer) as HTMLElement;
        this.swimmerButton = DOMHelper.querySelector(this.classSwimmerButton, this.swimmer) as HTMLElement;
        this.closeButton = DOMHelper.querySelector(SwimmerConstants.closeButtonSelector, this.swimmer) as HTMLElement;

        this.hideSwimmer();
    }

    initializeDdbox(): void {
        this.ddbox = DOMHelper.querySelector(this.ddboxSelector) as HTMLElement;
        this.ddboxButton = DOMHelper.querySelector(this.classDdboxButton, this.ddbox) as HTMLElement;
    }

    bindSwimmerEvents(): void {
        if (this.ddbox) {
            this.bindEventsDdboxScrollToPadlock();
        } else {
            this.bindEventsScrollToPadlock();
            this.bindEventsHideSwimmerAtHeightOfPadlock();
            this.bindEventCloseSwimmerManually();
        }
    }

    bindEventsDdboxScrollToPadlock(): void {
        if (this.ddboxButton && this.ddbox) {
            this.ddboxButton.addEventListener(EventType.CLICK, (event) => {
                event.preventDefault();
                this.utilizeElement(this.ddbox as Element, BigDataClickID.createAccount, false);
                this.scrollToPadlockForDdbox();
            })
        }
    }

    bindEventsScrollToPadlock(): void {
        if (this.swimmerButton) {
            this.swimmerButton.addEventListener(EventType.CLICK, (event) => {
                event.preventDefault();
                this.utilize(BigDataClickID.createAccount, false, true);
                this.scrollToElement();
            })
        }
    }

    bindEventsHideSwimmerAtHeightOfPadlock(): void {
        if (this.swimmer) {
            document.addEventListener(EventType.SCROLL, () => {
                this.toggleSwimmer();
            })
        }
    }

    toggleSwimmer(): void {
        const swimmerTop: number | undefined = this.swimmer?.offsetTop,
            sectionTop: number | undefined = this.section?.getBoundingClientRect().top,
            swimmerHeight: number = this.swimmerHeight || 250,
            height: number = this.getScreenHeight();

        if (sectionTop && swimmerTop && sectionTop <= swimmerTop && this.swimmerIsVisible) {
            this.hideSwimmer();

        } else if (sectionTop && (window.scrollY + (height - swimmerHeight) + 20) < sectionTop && !this.swimmerIsVisible) {
            this.showSwimmer();
        }
    }

    bindEventCloseSwimmerManually(): void {
        if (this.closeButton) {
            this.closeButton.addEventListener(EventType.CLICK, (event) => {
                event.preventDefault();
                this.closeSwimmerManually();
            })
        }
    }

    closeSwimmerManually(): void {
        this.closeButton?.classList.add(SwimmerConstants.closedManuallyClass);
    }

    scrollToPadlockForDdbox(): void {
        this.scrollToElement();
        this.closeDdbox();
    }

    scrollToElement() {
        if (this.section) {
            const top = this.section.offsetTop - window.innerHeight / 3;
            Utils.smoothScrollTo(top);
        }
    }

    getParentElement(isSwimmer = false): Element | null | undefined {
        const element: Element | null = isSwimmer ? this.swimmer : this.section;

        return element && element.closest(`[${MarketingClientConstants.placeholderDataSetAttribute}]`);
    }

    utilize(clickID = "", onlyBigData = false, isSwimmer = false): void {
        const parent = this.getParentElement(isSwimmer);

        if (parent) {
            this.utilizeElement(parent, clickID, onlyBigData);
        }
    }

    utilizeElement(element: Element, clickID: string, onlyBigData: boolean) {
        const condition = "brak",
            dataToSend = this.utilizationObject.prepareDataToSend(element, condition),
            attribute: Attribute = BigDataHelper.createAttribute(element, true, clickID);

        if (!onlyBigData) {
            this.utilizationObject.utilize(dataToSend);
        }

        BigData.sendDataOceanEvent(BigDataEventConstants.onClick, { ...dataToSend, ...attribute });
    }

    sendBigDataDisplay(ctaCategoryExtend: string): void {
        const parent = this.getParentElement(),
            condition = "brak";

        if (parent) {
            const dataToSend = this.utilizationObject.prepareDataToSend(parent, condition),
                attribute: Attribute = BigDataHelper.createAttribute(parent);

            attribute.ctaCategory = `${attribute.ctaCategory} ${ctaCategoryExtend}`;
            BigData.sendDataOceanEvent(BigDataEventConstants.onDisplay, { ...dataToSend, ...attribute });
        }
    }

    sendCapping(element: HTMLElement, onDisplay = false): void {
        this.capping.sendCappingEvent(element, onDisplay)
    }

    showSection(): void {
        this.section?.classList.remove(MarketingClientConstants.hiddenClassName);
    }

    hideSection(): void {
        this.section?.classList.add(MarketingClientConstants.hiddenClassName);
    }

    hideSwimmer(): void {
        this.swimmerIsVisible = false;
        this.swimmer?.classList.add(MarketingClientConstants.hiddenClassName);
    }

    showSwimmer(): void {
        this.swimmerIsVisible = true;
        this.swimmer?.classList.remove(MarketingClientConstants.hiddenClassName);
        this.swimmerHeight = this.swimmer?.getBoundingClientRect().height;
        this.sendDisplay();
    }

    showDdbox(): void {
        if(this.ddbox) {
            window["wyborcza_pl"]?.MarketingClient?.bannersObject?.ddbox?.showDdbox(this.ddbox);
        }
    }

    closeDdbox(): void {
        if(this.ddbox) {
            window["wyborcza_pl"]?.MarketingClient?.bannersObject?.ddbox?.closeDdbox(this.ddbox);
        }
    }

    sendDisplay(): void {
        if (this.swimmer && !this.swimmerWasVisible) {
            this.swimmerWasVisible = true;
            this.sendCapping(this.swimmer, true);
        }
    }

    getScreenHeight(): number {
        return window.innerHeight
            || document.documentElement.clientHeight
            || document.body.clientHeight;
    }
}
